import { Button, addToast } from '@octano/global-ui';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Card, Col, Row } from 'reactstrap';

import GoBackButton from '../../../../components/buttons/GoBackButton';
import { PathsLayouts } from '../../../../config/routes';
import { TranslationsKeys } from '../../../../locales/translations';
import { ActivityHour, createProgramRequest, ProgramUnit } from '../api';
import { ProgramFormLoader } from '../hooks/ProgramFormLoader';
import { ProgramForm, ProgramFormValues } from '../parts/Program/ProgramForm';

export function CreateProgram() {
  const history = useHistory();

  const goBack = useCallback(() => {
    history.push(`${PathsLayouts.nonStructured}/study-plans`);
  }, [history]);

  const { t } = useTranslation(
    TranslationsKeys.CONTINUOUS_EDUCATION_STUDY_PLANS,
  );

  const onSubmit = useCallback(
    async (values: ProgramFormValues) => {
      if (!values.modality || !values.school) {
        return;
      }

      const activityHours: ActivityHour[] = Object.entries(
        values.activityHours,
      ).map(([activityTypeId, hours]) => ({
        activityTypeId: activityTypeId,
        hours: Number(hours || 0),
      }));

      const units: ProgramUnit[] = values.units.map((unit) => ({
        id: unit?.id,
        name: unit.name,
        subjects: unit.subjects.map((subject) => ({
          id: subject.id,
        })),
      }));

      const requestBody = {
        name: values.name,
        code: values.code,
        academicLevelId: values.academicLevel?.value as number,
        modalityId: values.modality.value as number,
        schoolId: values.school.value as number,
        description: values.description,
        hasCertificate: values.hasCertificate,
        activityHours,
        units,
        imageId: values.curriculumImage?.id,
      };

      const { error } = await createProgramRequest(requestBody);
      if (!error) {
        addToast({
          icon: 'success',
          color: 'success',
          text: t(`common.msg.createProgramSuccess`),
          className: 'long-toast',
        });
        goBack();
        return;
      }

      if (error.data.code === 'CODE_IN_USE') {
        addToast({
          icon: 'error',
          color: 'danger',
          text: error.data.message,
          className: 'long-toast',
        });
        return;
      }

      addToast({
        icon: 'error',
        color: 'danger',
        text: t(`common.msg.createError`),
        className: 'long-toast',
      });
      return error.code;
    },
    [t, goBack],
  );

  return (
    <Card className="p-4 mt-2 mx-3">
      <div className="mb-4">
        <GoBackButton onClick={goBack} />
      </div>
      <ProgramFormLoader fetchImmediately>
        <ProgramForm onSubmit={onSubmit}>
          {({ isSubmitting }) => (
            <Row className="pt-5">
              <Col
                xs={{ order: 2, size: 12 }}
                md={{ order: 1, size: 4 }}
                className="ml-auto"
              >
                <div>
                  <Button
                    type="button"
                    text={t(`common.actions.cancel`)}
                    outlined
                    onClick={() => goBack()}
                    fullwidth
                  />
                </div>
              </Col>

              <Col xs={{ order: 1, size: 12 }} md={{ order: 2, size: 4 }}>
                <div className="pb-3 pb-md-0">
                  <Button
                    type="submit"
                    text={t(`common.actions.create`)}
                    loading={isSubmitting}
                    fullwidth
                  />
                </div>
              </Col>
            </Row>
          )}
        </ProgramForm>
      </ProgramFormLoader>
    </Card>
  );
}
